/**
 * Settings → Nightshift → Routes, as pure functions: filtering, grouping, the status each route
 * shows and the sentences the summary reads. The view renders these; the arithmetic lives here so
 * it can be tested without a DOM.
 */
import type { UiRouteEntry, UiRouteInventory, UiRouteModelUsage, UiRouteOwner, UiRouteRow, UiRouteStorage } from '@nightshift/ui-types';

import { harnessLabel } from './route-list.ts';

/** `''` means "all" for every select. */
export interface RoutesFilter {
  /** `''`, `nightshift`, or a project slug. */
  project: string;
  stage: string;
  harness: string;
  model: string;
  /** `''`, `any` for routes that name no machine, or a machine name. */
  machine: string;
  /** Hide inherited copies and show only lists stored where they are shown. */
  explicitOnly: boolean;
  /** Only routes that are retired, unknown, switched off or otherwise flagged. */
  problemsOnly: boolean;
}

export const EMPTY_ROUTES_FILTER: RoutesFilter = {
  project: '',
  stage: '',
  harness: '',
  model: '',
  machine: '',
  explicitOnly: false,
  problemsOnly: false,
};

export const ANY_MACHINE = 'any';

export function filterRoutes(entries: readonly UiRouteEntry[], filter: RoutesFilter): UiRouteEntry[] {
  return entries.filter((entry) => {
    if (filter.project === 'nightshift' && entry.owner.kind !== 'nightshift') return false;
    if (filter.project && filter.project !== 'nightshift' && entry.owner.projectSlug !== filter.project) return false;
    if (filter.stage && entry.stage !== filter.stage) return false;
    if (filter.harness && entry.harness !== filter.harness) return false;
    if (filter.model && entry.model !== filter.model) return false;
    if (filter.machine === ANY_MACHINE && entry.machine !== null) return false;
    if (filter.machine && filter.machine !== ANY_MACHINE && entry.machine !== filter.machine) return false;
    if (filter.explicitOnly && !entry.explicit) return false;
    if (filter.problemsOnly && entry.problems.length === 0) return false;
    return true;
  });
}

export function filterActive(filter: RoutesFilter): boolean {
  return (Object.keys(EMPTY_ROUTES_FILTER) as (keyof RoutesFilter)[]).some((key) => filter[key] !== EMPTY_ROUTES_FILTER[key]);
}

export interface RouteGroup {
  owner: UiRouteOwner;
  storage: UiRouteStorage;
  entries: UiRouteEntry[];
}

/** Consecutive entries of one owner, in inventory order (Nightshift, projects, tickets). */
export function groupRoutes(entries: readonly UiRouteEntry[]): RouteGroup[] {
  const groups: RouteGroup[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.owner.key === entry.owner.key) last.entries.push(entry);
    else groups.push({ owner: entry.owner, storage: entry.storage, entries: [entry] });
  }
  // A group is described by where its own lists live; inherited rows only say where they come from.
  for (const group of groups) group.storage = group.entries.find((entry) => entry.explicit)?.storage ?? group.storage;
  return groups;
}

export interface FilterOptions {
  stages: string[];
  harnesses: string[];
  models: string[];
  machines: string[];
}

export function filterOptions(inventory: UiRouteInventory): FilterOptions {
  const unique = (values: Iterable<string>) => [...new Set(values)];
  return {
    stages: unique(inventory.entries.map((entry) => entry.stage)),
    harnesses: unique(inventory.entries.map((entry) => entry.harness)).sort(),
    models: unique(inventory.entries.map((entry) => entry.model)).sort(),
    machines: unique(inventory.entries.flatMap((entry) => (entry.machine ? [entry.machine] : []))).sort(),
  };
}

export type RouteTone = 'danger' | 'warn' | 'ok';

/** The one-word status a route row shows, and how loudly. */
export function routeStatus(entry: Pick<UiRouteEntry, 'catalogue' | 'enabled' | 'problems'>): { tone: RouteTone; label: string } {
  if (entry.catalogue === 'retired') return { tone: 'danger', label: 'Retired' };
  if (!entry.enabled) return { tone: 'danger', label: 'Switched off' };
  if (entry.catalogue === 'unknown') return { tone: 'warn', label: 'Not in catalogue' };
  if (entry.problems.length > 0) return { tone: 'warn', label: 'Check' };
  return { tone: 'ok', label: 'Current' };
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "claude-opus-5 is used in 23 routes across 4 projects and 2 tickets". */
export function usageSentence(usage: UiRouteModelUsage): string {
  const where: string[] = [];
  if (usage.projects > 0) where.push(plural(usage.projects, 'project'));
  if (usage.tickets > 0) where.push(plural(usage.tickets, 'ticket'));
  const scope = where.length > 0 ? ` across ${where.join(' and ')}` : ' in the Nightshift-wide lists';
  const stored = usage.explicit === usage.routes ? '' : ` (${usage.explicit} stored, the rest inherited)`;
  return `${usage.model} is used in ${plural(usage.routes, 'route')}${scope}${stored}`;
}

const STORAGE_LABELS: Record<UiRouteStorage, string> = {
  settings: 'Nightshift settings',
  'project-configuration': 'Project configuration',
  'night-manager': 'Night-manager defaults',
  'night-manager-run': 'Enabled night-manager run',
  'ticket-overrides': 'Ticket overrides',
  'coded-default': 'Coded default',
};

export function storageLabel(storage: UiRouteStorage): string {
  return STORAGE_LABELS[storage];
}

/** Where the list a group shows lives, for its heading. */
export function groupNote(group: RouteGroup): string {
  if (group.owner.kind === 'nightshift') return 'Settings → Nightshift → Models';
  if (group.owner.kind === 'ticket') return 'Ticket drawer → Overrides';
  if (group.storage === 'project-configuration') return 'Project configuration';
  if (group.storage === 'night-manager' || group.storage === 'night-manager-run') return 'Night manager';
  if (group.entries.every((entry) => !entry.explicit)) return 'Inherits every list';
  return storageLabel(group.storage);
}

/** `mediaservervm · Codex · gpt-5.6-luna · low`, with "any machine" spelled out. */
export function routeText(row: Pick<UiRouteRow, 'machine' | 'harness' | 'model' | 'effort'>): string {
  return [row.machine ?? 'any machine', harnessLabel(row.harness), row.model, row.effort].filter(Boolean).join(' · ');
}

/** The harness + model combinations the inventory names, with every thinking level seen. */
export function replaceSources(inventory: UiRouteInventory): Array<{ key: string; harness: string; model: string; efforts: Array<string | null>; usage: UiRouteModelUsage }> {
  return inventory.models
    .filter((usage) => usage.explicit > 0 || inventory.entries.some((entry) => entry.editable && entry.model === usage.model && entry.harness === usage.harness))
    .map((usage) => ({ key: `${usage.harness} ${usage.model}`, harness: usage.harness, model: usage.model, efforts: usage.efforts, usage }));
}
